import type {
  GetStaffRegisterResponse,
  GetTimeReportsV2SummaryResponse,
  TimeReportsV2Filter,
  TimeReportsV2Group,
  TimeReportsV2SortBy,
} from "services/api/company/timeReports.service";

export type TimeReportsV2GroupByOption = "person" | "team";

export interface TimeReportsV2Preferences {
  groupByOption: TimeReportsV2GroupByOption;
}

export interface TimeReportsV2ListQuery {
  fromDate: string;
  toDate: string;
  sortBy: TimeReportsV2SortBy;
  filters?: TimeReportsV2Filter[];
  workGroupIds: number[];
  workerIds: number[];
  pageSize: number;
}

export interface TimeReportsV2SummaryQuery {
  fromDate: string;
  toDate: string;
  workGroupIds: number[];
  workerIds: number[];
}

export interface TimeReportsV2StaffRegisterQuery {
  fromDate: string;
  toDate: string;
}

export interface TimeReportsV2ListData {
  items: TimeReportsV2Group[];
  total: number;
}

export type TimeReportsV2Actions =
  | {
      type: "SET_LIST_LOADING";
      payload: { value: boolean; requestId: number };
    }
  | {
      type: "SET_LIST_LOADING_MORE";
      payload: { value: boolean; requestId: number };
    }
  | {
      type: "SET_LIST_REQUEST_ERROR";
      payload: TimeReportsV2State["listError"];
    }
  | {
      type: "SET_LIST";
      payload: {
        data: TimeReportsV2ListData | null;
        requestId: number;
        append: boolean;
        page: number;
        pageSize: number;
      };
    }
  | { type: "SET_ACTIVE_LIST_REQUEST_ID"; payload: number | null }
  | { type: "RESET_LIST" }
  | { type: "SET_LIST_QUERY"; payload: TimeReportsV2ListQuery | null }
  | { type: "SET_SUMMARY_QUERY"; payload: TimeReportsV2SummaryQuery | null }
  | {
      type: "SET_SUMMARY_LOADING";
      payload: { value: boolean; requestId: number };
    }
  | { type: "SET_ACTIVE_SUMMARY_REQUEST_ID"; payload: number | null }
  | {
      type: "SET_SUMMARY_REQUEST_ERROR";
      payload: TimeReportsV2State["summaryError"];
    }
  | {
      type: "SET_SUMMARY";
      payload: {
        data: GetTimeReportsV2SummaryResponse | null;
        requestId: number;
      };
    }
  | {
      type: "SET_COUNT_LOADING";
      payload: { value: boolean; requestId: number };
    }
  | { type: "SET_ACTIVE_COUNT_REQUEST_ID"; payload: number | null }
  | {
      type: "SET_COUNT_REQUEST_ERROR";
      payload: TimeReportsV2State["countError"];
    }
  | {
      type: "SET_COUNT";
      payload: { value: number; requestId: number };
    }
  | {
      type: "SET_PREFERENCES_REQUEST_ERROR";
      payload: TimeReportsV2State["preferencesError"];
    }
  | {
      type: "SET_STAFF_REGISTER_LOADING";
      payload: { value: boolean; requestId: number };
    }
  | { type: "SET_ACTIVE_STAFF_REGISTER_REQUEST_ID"; payload: number | null }
  | {
      type: "SET_STAFF_REGISTER_REQUEST_ERROR";
      payload: TimeReportsV2State["staffRegisterError"];
    }
  | {
      type: "SET_STAFF_REGISTER";
      payload: {
        data: GetStaffRegisterResponse | null;
        requestId: number;
      };
    }
  | {
      type: "SET_STAFF_REGISTER_QUERY";
      payload: TimeReportsV2StaffRegisterQuery | null;
    }
  | { type: "SET_PREFERENCES"; payload: TimeReportsV2Preferences };

export interface TimeReportsV2State {
  isListLoading: boolean;
  isListLoadingMore: boolean;
  list: TimeReportsV2ListData | null;
  page: number;
  pageSize: number;
  listError: { message: string; err: Error } | null;
  activeListRequestId: number | null;
  listQuery: TimeReportsV2ListQuery | null;
  summary: GetTimeReportsV2SummaryResponse | null;
  isSummaryLoading: boolean;
  summaryError: { message: string; err: Error } | null;
  activeSummaryRequestId: number | null;
  summaryQuery: TimeReportsV2SummaryQuery | null;
  count: number;
  isCountLoading: boolean;
  countError: { message: string; err: Error } | null;
  activeCountRequestId: number | null;
  preferencesError: { message: string; err: Error } | null;
  staffRegister: GetStaffRegisterResponse | null;
  isStaffRegisterLoading: boolean;
  staffRegisterError: { message: string; err: Error } | null;
  activeStaffRegisterRequestId: number | null;
  staffRegisterQuery: TimeReportsV2StaffRegisterQuery | null;
  preferences: TimeReportsV2Preferences;
}

export const timeReportsV2InitState: TimeReportsV2State = {
  isListLoading: false,
  isListLoadingMore: false,
  list: null,
  page: 0,
  pageSize: 0,
  listError: null,
  activeListRequestId: null,
  listQuery: null,
  summary: null,
  isSummaryLoading: false,
  summaryError: null,
  activeSummaryRequestId: null,
  summaryQuery: null,
  count: 0,
  isCountLoading: false,
  countError: null,
  activeCountRequestId: null,
  preferencesError: null,
  staffRegister: null,
  isStaffRegisterLoading: false,
  staffRegisterError: null,
  activeStaffRegisterRequestId: null,
  staffRegisterQuery: null,
  preferences: {
    groupByOption: "person",
  },
};

export const timeReportsV2StateReducer = (
  state: TimeReportsV2State,
  action: TimeReportsV2Actions,
): TimeReportsV2State => {
  switch (action.type) {
    case "SET_LIST_LOADING": {
      if (action.payload.requestId !== state.activeListRequestId) return state;
      return { ...state, isListLoading: action.payload.value };
    }
    case "SET_LIST_LOADING_MORE": {
      if (action.payload.requestId !== state.activeListRequestId) return state;
      return { ...state, isListLoadingMore: action.payload.value };
    }
    case "SET_LIST_REQUEST_ERROR": {
      return { ...state, listError: action.payload };
    }
    case "SET_LIST": {
      if (action.payload.requestId !== state.activeListRequestId) return state;
      if (!action.payload.data) {
        return {
          ...state,
          list: null,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
        };
      }
      const nextItems = action.payload.append
        ? [...(state.list?.items ?? []), ...action.payload.data.items]
        : action.payload.data.items;
      return {
        ...state,
        list: {
          items: nextItems,
          total: action.payload.data.total,
        },
        page: action.payload.page,
        pageSize: action.payload.pageSize,
      };
    }
    case "SET_ACTIVE_LIST_REQUEST_ID": {
      return { ...state, activeListRequestId: action.payload };
    }
    case "RESET_LIST": {
      return {
        ...state,
        list: null,
        page: 0,
        pageSize: 0,
        listError: null,
        isListLoading: false,
        isListLoadingMore: false,
        activeListRequestId: null,
      };
    }
    case "SET_LIST_QUERY": {
      return { ...state, listQuery: action.payload };
    }
    case "SET_SUMMARY_QUERY": {
      return { ...state, summaryQuery: action.payload };
    }
    case "SET_SUMMARY_LOADING": {
      if (action.payload.requestId !== state.activeSummaryRequestId)
        return state;
      return { ...state, isSummaryLoading: action.payload.value };
    }
    case "SET_ACTIVE_SUMMARY_REQUEST_ID": {
      return { ...state, activeSummaryRequestId: action.payload };
    }
    case "SET_SUMMARY_REQUEST_ERROR": {
      return { ...state, summaryError: action.payload };
    }
    case "SET_SUMMARY": {
      if (action.payload.requestId !== state.activeSummaryRequestId)
        return state;
      return { ...state, summary: action.payload.data };
    }
    case "SET_COUNT_LOADING": {
      if (action.payload.requestId !== state.activeCountRequestId) return state;
      return { ...state, isCountLoading: action.payload.value };
    }
    case "SET_ACTIVE_COUNT_REQUEST_ID": {
      return { ...state, activeCountRequestId: action.payload };
    }
    case "SET_COUNT_REQUEST_ERROR": {
      return { ...state, countError: action.payload };
    }
    case "SET_COUNT": {
      if (action.payload.requestId !== state.activeCountRequestId) return state;
      return { ...state, count: action.payload.value };
    }
    case "SET_PREFERENCES_REQUEST_ERROR": {
      return { ...state, preferencesError: action.payload };
    }
    case "SET_STAFF_REGISTER_LOADING": {
      if (action.payload.requestId !== state.activeStaffRegisterRequestId) {
        return state;
      }
      return { ...state, isStaffRegisterLoading: action.payload.value };
    }
    case "SET_ACTIVE_STAFF_REGISTER_REQUEST_ID": {
      return { ...state, activeStaffRegisterRequestId: action.payload };
    }
    case "SET_STAFF_REGISTER_REQUEST_ERROR": {
      return { ...state, staffRegisterError: action.payload };
    }
    case "SET_STAFF_REGISTER": {
      if (action.payload.requestId !== state.activeStaffRegisterRequestId) {
        return state;
      }
      return { ...state, staffRegister: action.payload.data };
    }
    case "SET_STAFF_REGISTER_QUERY": {
      return { ...state, staffRegisterQuery: action.payload };
    }
    case "SET_PREFERENCES": {
      return { ...state, preferences: action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};
